import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, ShieldCheck } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

function NodeCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf, w, h, nodes = [];
    const DPR = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * DPR;
      canvas.height = h * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      const count = Math.min(64, Math.floor((w * h) / 24000));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        r: Math.random() * 1.6 + 0.6,
      }));
    };
    resize();
    window.addEventListener("resize", resize);

    const step = () => {
      ctx.clearRect(0, 0, w, h);
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      }
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          if (d < 140) {
            ctx.strokeStyle = `rgba(0, 240, 255, ${(1 - d / 140) * 0.13})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      for (const n of nodes) {
        ctx.fillStyle = "rgba(0, 240, 255, 0.45)";
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(step);
    };
    step();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} className="absolute inset-0 h-full w-full" aria-hidden="true" />;
}

const lineReveal = (text, i, extra = "") => (
  <span key={i} className="block overflow-hidden">
    <motion.span
      className={`block ${extra}`}
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, ease: EASE, delay: 0.25 + i * 0.13 }}
    >
      {text}
    </motion.span>
  </span>
);

const BADGES = ["100% conforme norma CNE", "99.99% uptime de red", "24/7 soporte regional"];

const Hero = () => {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 200]);
  const fadeBg = useTransform(scrollY, [0, 700], [1, 0.25]);

  return (
    <section id="hero" className="relative flex min-h-screen flex-col overflow-hidden bg-obsidian" data-testid="hero-section">
      <motion.div style={{ y: yBg, opacity: fadeBg }} className="absolute inset-0">
        <NodeCanvas />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 55% at 50% 0%, rgba(0,240,255,0.10), transparent 65%), linear-gradient(to bottom, rgba(9,10,15,0.25) 0%, rgba(9,10,15,0.72) 62%, #090A0F 100%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.14]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.35) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            maskImage: "radial-gradient(ellipse 80% 60% at 50% 30%, black, transparent 75%)",
            WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 30%, black, transparent 75%)",
          }}
        />
      </motion.div>

      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-5 pb-16 pt-36 sm:px-8 sm:pt-40">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          className="mb-7 flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[10px] font-semibold uppercase tracking-[0.3em] text-neon sm:text-[11px]"
          data-testid="hero-eyebrow"
        >
          <ShieldCheck className="h-4 w-4" />
          Tecnología e Innovación de Occidente — México
        </motion.p>

        <h1 className="max-w-5xl font-heading text-[2.55rem] font-extrabold leading-[1.04] tracking-[-0.03em] text-white sm:text-6xl lg:text-7xl xl:text-[5.1rem]">
          {lineReveal("Infraestructura tecnológica", 0)}
          {lineReveal("y cumplimiento de", 1)}
          {lineReveal(
            <>
              grado gubernamental<span className="text-neon">.</span>
            </>,
            2
          )}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
          className="mt-7 max-w-xl font-body text-base leading-relaxed text-slate-400 sm:text-lg"
          data-testid="hero-subtitle"
        >
          Soluciones integrales de TI, auditoría de sistemas electorales (CNE), ciberseguridad y
          transformación digital para el Occidente de México.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#servicios"
            data-testid="hero-cta-explorar"
            className="group flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 font-body text-sm font-semibold text-obsidian transition-all duration-300 hover:bg-white"
          >
            Explorar soluciones
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#sectores"
            data-testid="hero-cta-cne"
            className="rounded-full border border-white/20 bg-white/5 px-7 py-3.5 font-body text-sm font-semibold text-white backdrop-blur transition-all duration-300 hover:border-neon/60 hover:text-neon"
          >
            Cumplimiento CNE
          </a>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.15 }}
          className="mt-14 flex flex-wrap gap-x-8 gap-y-3"
          data-testid="hero-badges"
        >
          {BADGES.map((b) => (
            <li key={b} className="flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400 sm:text-[11px]">
              <span className="h-1.5 w-1.5 rounded-full bg-neon" />
              {b}
            </li>
          ))}
        </motion.ul>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="relative z-10 hidden justify-center pb-8 md:flex"
        aria-hidden="true"
      >
        <div className="flex flex-col items-center gap-2 text-slate-500">
          <span className="font-mono text-[9px] uppercase tracking-[0.3em]">Desliza</span>
          <ArrowDown className="h-4 w-4 animate-bounce" />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
